import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Clock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, arabicError } from "../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("ar-SY", { timeZone: "Asia/Damascus", dateStyle: "medium", timeStyle: "short" }) : "");

// 403 bodies for blob downloads arrive as Blob → decode to detect EXPORT_APPROVAL_REQUIRED.
export const exportErrorDetail = async (e) => {
  let d = e?.response?.data;
  if (d instanceof Blob) { try { d = JSON.parse(await d.text()); } catch { d = null; } }
  return d?.detail ?? d;
};

export const handleExportError = async (e) => {
  const det = await exportErrorDetail(e);
  if (det?.code === "EXPORT_APPROVAL_REQUIRED") {
    toast.error(det.state === "pending" ? "طلب الموافقة على التصدير قيد الانتظار لدى الإدارة" : "تصدير Excel يتطلب موافقة الإدارة — اطلب الموافقة أولاً");
    window.dispatchEvent(new Event("export-approval-changed"));
    return true;
  }
  toast.error(typeof det === "string" ? det : arabicError(e));
  return false;
};

export default function ExportApprovalStatus({ className = "" }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { const r = await api.get("/export-approval/status"); setSt(r.data); } catch { setSt(null); } }, []);
  useEffect(() => { load(); window.addEventListener("export-approval-changed", load); return () => window.removeEventListener("export-approval-changed", load); }, [load]);
  if (!st || st.state === "exempt") return null;
  const request = async () => {
    setBusy(true);
    try { const r = await api.post("/export-approval/request"); setSt(r.data); toast.success(r.data.created ? "تم إرسال طلب الموافقة إلى الإدارة" : "طلبك قيد الانتظار بالفعل"); }
    catch (e) { toast.error(arabicError(e)); } finally { setBusy(false); }
  };
  const map = {
    approved: ["bg-emerald-50 border-emerald-200 text-emerald-800", ShieldCheck, `التصدير متاح حتى ${fmt(st.valid_until)}`],
    pending: ["bg-amber-50 border-amber-200 text-amber-800", Clock, `طلب الموافقة قيد الانتظار (${fmt(st.requested_at)})`],
    expired: ["bg-rose-50 border-rose-200 text-rose-800", ShieldAlert, `انتهت صلاحية الموافقة (${fmt(st.valid_until)})`],
    rejected: ["bg-rose-50 border-rose-200 text-rose-800", ShieldAlert, "رُفض طلب التصدير السابق"],
    none: ["bg-[#F5FAFB] border-[#DCEBEE] text-[#09637E]", ShieldAlert, "تصدير Excel يتطلب موافقة الإدارة"],
  };
  const [cls, Icon, text] = map[st.state] || map.none;
  return (
    <div className={`flex flex-wrap items-center gap-2 text-xs font-bold border rounded-xl px-3 py-2 min-w-0 ${cls} ${className}`} data-testid="export-approval-status" data-state={st.state}>
      <Icon size={14} className="shrink-0" /> <span className="min-w-0 break-words">{text}</span>
      {(st.state === "none" || st.state === "expired" || st.state === "rejected") && (
        <button onClick={request} disabled={busy} data-testid="export-approval-request-btn"
          className="h-8 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-xs font-bold disabled:opacity-60">
          {busy ? "…" : "طلب الموافقة"}
        </button>
      )}
    </div>
  );
}
